import { useLingui } from '@lingui/react/macro'
import { Toggle } from 'designSystem/components/Toggle'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { DocChatFeedbackDialog } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/DocChatFeedbackDialog'
import type { DocChatFeedbackBase } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatFeedbackBase'
import { useDocChatFeedbackDialog } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatFeedbackDialog'
import { useSubmitDocChatFeedback } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useSubmitDocChatFeedback'
import type { DocChatFeedbackReason } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatFeedback'
import { ThumbsDown } from 'lucide-react'
import React, { useCallback } from 'react'

export type DocChatFeedbackButtonProps = {
  rated: boolean
  disabled: boolean
  feedback: DocChatFeedbackBase
  onRated: (reasons: DocChatFeedbackReason[], hasComment: boolean) => void
  onCleared: () => void
}

/** Thumbs-down that collects what was wrong with one answer and saves it. */
export function DocChatFeedbackButton({
  rated,
  disabled,
  feedback,
  onRated,
  onCleared,
}: DocChatFeedbackButtonProps): React.ReactElement {
  const { t } = useLingui()
  const submitFeedback = useSubmitDocChatFeedback()
  const dialog = useDocChatFeedbackDialog()
  const { open: openDialog, submit } = dialog

  const handlePressedChange = useCallback(
    (pressed: boolean) => {
      if (pressed) openDialog()
      else onCleared()
    },
    [onCleared, openDialog]
  )

  const handleDialogSubmit = useCallback(
    (reasons: DocChatFeedbackReason[], comment: string) => {
      void submit(() => submitFeedback({ ...feedback, reasons, comment })).then(
        (sent) => {
          if (sent) onRated(reasons, comment.trim().length > 0)
        }
      )
    },
    [feedback, onRated, submit, submitFeedback]
  )

  const label = rated ? t`Feedback sent` : t`Not helpful`

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Toggle
            aria-label={label}
            pressed={rated}
            disabled={disabled}
            onPressedChange={handlePressedChange}>
            <ThumbsDown size={14} fill={rated ? 'currentColor' : 'none'} />
          </Toggle>
        </TooltipTrigger>
        <TooltipContent side='top'>{label}</TooltipContent>
      </Tooltip>
      {dialog.isOpen ? (
        <DocChatFeedbackDialog
          open
          isSubmitting={dialog.isSubmitting}
          submitted={dialog.submitted}
          error={dialog.error}
          onOpenChange={dialog.onOpenChange}
          onSubmit={handleDialogSubmit}
        />
      ) : null}
    </>
  )
}
